/**
 * Tool Validation Utility
 *
 * Validates tool calls against enabled tools for the current request.
 * Prevents AI models from executing tools that weren't explicitly enabled,
 * which can happen when the model "remembers" tools from previous requests.
 */

/**
 * Extracts tool names from a tools array and returns them as a Set for O(1) lookup
 * @param {Array} tools - Array of tool definitions with OpenAI function calling format
 * @returns {Set<string>} Set of enabled tool names
 */
export function getEnabledToolNames(tools) {
  if (!tools || !Array.isArray(tools)) {
    return new Set();
  }

  const toolNames = tools
    .filter((tool) => tool && tool.type === "function" && tool.function?.name)
    .map((tool) => tool.function.name);

  return new Set(toolNames);
}

/**
 * Validates if a tool call is authorized for the current request
 * @param {string} toolName - The name of the tool being called
 * @param {Set<string>} enabledToolNames - Set of tool names enabled for this request
 * @returns {Object} Validation result with isValid boolean and error message if invalid
 */
export function validateToolCall(toolName, enabledToolNames) {
  if (!toolName) {
    return {
      isValid: false,
      error: "Tool name is required",
    };
  }

  if (!enabledToolNames || enabledToolNames.size === 0) {
    return {
      isValid: false,
      error: `Tool '${toolName}' is not authorized. No tools are enabled for this request.`,
    };
  }

  if (!enabledToolNames.has(toolName)) {
    const enabledList = Array.from(enabledToolNames).join(", ");
    return {
      isValid: false,
      error: `Tool '${toolName}' is not authorized for this request. Enabled tools: ${enabledList}`,
    };
  }

  return {
    isValid: true,
  };
}

/**
 * Validates multiple tool calls at once
 * @param {Array} toolCalls - Array of tool call objects with function.name
 * @param {Set<string>} enabledToolNames - Set of tool names enabled for this request
 * @returns {Object} Validation result with invalid tool calls and errors
 */
export function validateToolCalls(toolCalls, enabledToolNames) {
  if (!toolCalls || !Array.isArray(toolCalls)) {
    return {
      isValid: true,
      invalidCalls: [],
    };
  }

  const invalidCalls = [];

  for (const toolCall of toolCalls) {
    const toolName = toolCall?.function?.name || toolCall?.name;
    if (!toolName) continue;

    const validation = validateToolCall(toolName, enabledToolNames);
    if (!validation.isValid) {
      invalidCalls.push({
        toolName,
        toolCall,
        error: validation.error,
      });
    }
  }

  return {
    isValid: invalidCalls.length === 0,
    invalidCalls,
  };
}

/**
 * Creates a user-friendly error message for unauthorized tool usage
 * @param {string} toolName - The tool that was attempted
 * @param {Set<string>} enabledToolNames - Tools that were actually enabled
 * @returns {string} User-friendly error message
 */
export function createToolAuthorizationError(toolName, enabledToolNames) {
  const enabledList =
    enabledToolNames.size > 0
      ? Array.from(enabledToolNames).join(", ")
      : "none";

  return `I attempted to use the '${toolName}' tool, but it's not enabled for this request. Please enable this tool to use this feature.\n\nEnabled tools: ${enabledList}`;
}
